// Imports
import { faArrowRotateLeft, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import styled, { css } from 'styled-components/macro';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { SingleFileProps } from '../../../../../../../shareables/foundation/front-end/components/forms/fields/file';
import MultiFieldContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import convertCropToBlob from '../../../../../../../shareables/front-end/utils/convert-crop-to-blob';
import UserAvatar from '../../../../../shared-components/user-avatar';
import { visuallyHiddenLabelStyle } from '../../../../../shared-components/visually-hidden-label';
import PhotoUpload from './photo-upload';


// Styled components
const ProfilePhotoWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	margin-bottom: 1.25rem;
	margin-top: 0.625rem;
	align-items: center;
	
	> .ReactCrop {
		width: 100%;
		background-color: #fff;
	}
`;

const ProfileAvatar = styled(UserAvatar)<{ size: 'FULL' | 'HALF' }>`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: ${({ size }) => (size === 'FULL' ? '2.5rem' : '1.25rem')};
`;

const ImageBeingCropped = styled.img`
	max-height: 100%;
	min-width: 100%;
`;

const Image = styled.img<{ removePhoto: boolean; size: 'FULL' | 'HALF' }>`
	border-radius: 50%;
	
	${({ removePhoto }) => removePhoto && 'opacity: 0.5;'}
	width: ${({ size }) => (size === 'FULL' ? '6.25rem' : '3.125rem')};
	height: ${({ size }) => (size === 'FULL' ? '6.25rem' : '3.125rem')};
`;

const StyledMultiFieldContainer = styled(MultiFieldContainer)<{ $hideLabel?: boolean }>`
	display: flex;
	align-items: center;
	
	> div {
		justify-content: flex-start;
	}
	
	${({ $hideLabel }) =>
		$hideLabel &&
		css`
			label {
				${visuallyHiddenLabelStyle}
			}
		`}
`;


// Define the accepted props
interface Props {
	setNewProfilePhoto: (newProfilePhoto: File | undefined) => void;
	setRemovePhoto: (removePhoto: boolean) => void;
	removePhoto: boolean;
	defaultAvatarSize: 'FULL' | 'HALF';
	profilePictureSetter?: MutableRefObject<((file: File | null) => void) | undefined>;
	resetCanvasSource?: MutableRefObject<(() => void) | undefined>;
	fileRef?: RefObject<HTMLInputElement>;
	profilePhotoURL?: string | null;
	memberFirstName?: string;
	memberLastName?: string;
	useDefaultColor?: true;
	parentParameter?: string;
	storybookCanvasSource?: string;
}


// Function component
const UploadProfilePhoto: React.FC<Props> = ({
	setNewProfilePhoto,
	setRemovePhoto,
	removePhoto,
	defaultAvatarSize,
	profilePictureSetter,
	resetCanvasSource,
	fileRef,
	profilePhotoURL,
	memberFirstName,
	memberLastName,
	useDefaultColor,
	parentParameter,
	storybookCanvasSource,
}) => {
	// Use state
	const [crop, setCrop] = useState<PercentCrop | undefined>();
	const [canvasSource, setCanvasSource] = useState<string | null>(storybookCanvasSource ?? null);
	
	
	// Use ref
	const imageRef = useRef<HTMLImageElement>();
	
	
	// Use Redux functionality
	const user = useAppSelector((state) => state.common.user);
	
	
	// Initialize name
	const firstName = memberFirstName ?? user.firstName ?? undefined;
	const lastName = memberLastName ?? user.lastName ?? undefined;
	
	
	// Register canvas source reset for parent to call after form completion
	useEffect(() => {
		if (resetCanvasSource) {
			resetCanvasSource.current = () => setCanvasSource(null);
			
			return () => {
				resetCanvasSource.current = undefined;
			};
		}
	}, [resetCanvasSource, setCanvasSource]);
	
	
	// Handle user selecting an image
	const handleFileChange: SingleFileProps['onChange'] = (file) => {
		if (file === null) {
			setCanvasSource(null);
			setNewProfilePhoto(undefined);
		} else {
			setCanvasSource(URL.createObjectURL(file));
		}
	};
	
	
	// Handle converting crop to blob
	const cropConverter = (crop: PercentCrop) => {
		// If crop has not been set, do nothing
		if (!crop || !imageRef.current) {
			return;
		}
		
		
		// Get blob
		convertCropToBlob(
			imageRef.current,
			convertToPixelCrop(crop, imageRef.current.width, imageRef.current.height),
			[
				(blob) => {
					if (!blob) {
						return;
					}
					
					setNewProfilePhoto(new window.File([blob], 'profile.png', { type: 'image/png' }));
				},
				'image/png',
				1,
			],
			{
				aspect: 1,
				max: {
					width: 1000,
					height: 1000,
				},
			}
		);
	};
	
	
	// Handle image loading into crop
	const handleImageLoaded: React.ReactEventHandler<HTMLImageElement> = ({ currentTarget: image }) => {
		// Set image ref
		imageRef.current = image;
		
		
		// Decide and set what the initial crop should be
		const width = image.width > image.height ? image.height : image.width;
		
		const centeredCrop = centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: width,
				},
				1,
				image.width,
				image.height
			),
			image.width,
			image.height
		);
		
		
		// Set the initial crop centered vertically
		setCrop(centeredCrop);
		
		
		// Set the new banner based on initial crop
		cropConverter(centeredCrop);
		
		
		// Required when altering the crop per the documentation
		return false;
	};
	
	
	// Handle clearing a newly uploaded photo
	const handleRemoveNewPhoto = () => {
		profilePictureSetter?.current?.(null);
		
		if (fileRef?.current) {
			fileRef.current.value = '';
		}
	};
	
	
	// Handle undoing a newly uploaded photo
	const handleUndoUpload = () => {
		handleRemoveNewPhoto();
		setCanvasSource(null);
		setNewProfilePhoto(undefined);
	};
	
	
	// Handle the remove/restore button click
	const handleRemoveOrRestorePhoto = () => {
		// Always clear any newly selected photo
		handleUndoUpload();
		
		
		// Toggle the remove state for the existing photo
		if (profilePhotoURL) {
			setRemovePhoto(!removePhoto);
		}
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<h4>Profile photo</h4>
			<ProfilePhotoWrapper>
				{canvasSource && !removePhoto ? (
					<ReactCrop
						crop={crop}
						onChange={(_, crop) => setCrop(crop)}
						onComplete={(_, crop) => cropConverter(crop)}
						minWidth={100}
						minHeight={100}
						aspect={1}
						ruleOfThirds
					>
						<ImageBeingCropped onLoad={handleImageLoaded} src={canvasSource ?? ''} alt='Uncropped profile photo' />
					</ReactCrop>
				) : profilePhotoURL ? (
					<Image size={defaultAvatarSize} removePhoto={removePhoto} src={profilePhotoURL} alt='Profile photo' />
				) : (
					<ProfileAvatar
						size={defaultAvatarSize}
						firstName={firstName}
						lastName={lastName}
						useDefaultColor={useDefaultColor}
						round
					/>
				)}
				
				<StyledMultiFieldContainer $hideLabel={Boolean(profilePhotoURL)}>
					{!removePhoto && (
						<PhotoUpload
							accept='image/*'
							name={`${parentParameter ? `${parentParameter}.profilePhoto` : 'profilePhoto'}`}
							parentParameter={parentParameter}
							exclude
							onChange={handleFileChange}
							label='Upload new photo'
							disabled={removePhoto}
							prompt={profilePhotoURL ? 'Change photo' : undefined}
							bindSetter={(setter) => {
								if (profilePictureSetter) {
									profilePictureSetter.current = setter;
								}
							}}
							ref={fileRef}
						/>
					)}
					
					{canvasSource && !removePhoto && (
						<Button type='button' color='danger' onClick={handleUndoUpload}>
							<FontAwesomeIcon icon={faArrowRotateLeft} />
							<span>Undo</span>
						</Button>
					)}
					
					{profilePhotoURL && !canvasSource && (
						<Button type='button' color='danger' onClick={handleRemoveOrRestorePhoto}>
							<FontAwesomeIcon icon={removePhoto ? faArrowRotateLeft : faTrash} />
							<span>{removePhoto ? 'Restore photo' : 'Remove photo'}</span>
						</Button>
					)}
				</StyledMultiFieldContainer>
			</ProfilePhotoWrapper>
		</React.Fragment>
	);
};

export default UploadProfilePhoto;
